import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';

export default function OrganizationUnitAccessPanel() {
  const { currentEntidade } = useEntidadeContext();
  const [data, setData] = useState(null);
  const [employeeId, setEmployeeId] = useState('');
  const [inherit, setInherit] = useState(true);
  const [selected, setSelected] = useState([]);
  const [payrollUnit, setPayrollUnit] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    const response = await axios.get('/api/unit-access', { params: { entidade_id: currentEntidade.id } });
    setData(response.data);
    return response.data;
  }, [currentEntidade?.id]);
  useEffect(() => {
    let active = true;
    setData(null); setEmployeeId(''); setError(''); setNotice('');
    if (currentEntidade?.id) axios.get('/api/unit-access', { params: { entidade_id: currentEntidade.id } })
      .then(r => { if (active) setData(r.data); })
      .catch(e => { if (active) setError(e.response?.data?.message || 'Falha ao carregar os acessos.'); });
    return () => { active = false; };
  }, [currentEntidade?.id]);
  const choose = id => {
    setEmployeeId(id); setReason(''); setNotice(''); setError('');
    const employee = data.employees.find(e => String(e.id) === id);
    setInherit(employee?.inherit_primary ?? true);
    setPayrollUnit(employee?.payroll_unit_id ? String(employee.payroll_unit_id) : '');
    setSelected((employee?.unit_ids || []).filter(unitId => data.units.some(u => u.id === unitId && u.active)));
  };
  const employee = data?.employees.find(e => String(e.id) === employeeId);
  const save = async event => {
    event.preventDefault(); setBusy(true); setError(''); setNotice('');
    try {
      await axios.put(`/api/unit-access/${employee.id}`, {
        inherit_primary: inherit, unit_ids: inherit ? [] : selected,
        reason: reason.trim(), revision: employee.revision,
      });
      await load(); setReason(''); setNotice('Acessos atualizados com sucesso.');
    } catch (e) {
      setError(Object.values(e.response?.data?.errors || {}).flat().join(' ') || e.response?.data?.message || 'Falha ao salvar os acessos.');
    } finally { setBusy(false); }
  };
  const savePayroll = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      await axios.put(`/api/unit-access/${employee.id}/payroll`, { unit_id: payrollUnit ? Number(payrollUnit) : null, expected_unit_id: employee.payroll_unit_id || null, reason: reason.trim() });
      await load(); setReason(''); setNotice('Unidade da folha atualizada. Os históricos anteriores foram preservados.');
    } catch (e) { setError(e.response?.data?.message || 'Falha ao atualizar a unidade da folha.'); }
    finally { setBusy(false); }
  };
  return <Card className="border-0 shadow-sm mt-4">
    <Card.Header className="bg-white fw-bold">Equipe e acesso por unidade</Card.Header>
    <Card.Body>
      <p className="text-muted">Defina em quais unidades cada profissional poderá trabalhar. O representante legal mantém acesso a todas as unidades da entidade.</p>
      <Alert variant="info">Os funcionários podem escolher suas unidades no cabeçalho. O acesso à unidade e as permissões do perfil são verificados separadamente.</Alert>
      {error && <Alert variant="danger">{error}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}
      {!data ? (!error && <Spinner animation="border" />) : <Form onSubmit={save}>
        <Form.Group controlId="unit-access-employee" className="mb-3">
          <Form.Label>Funcionário</Form.Label>
          <Form.Select value={employeeId} disabled={busy} onChange={e => choose(e.target.value)}>
            <option value="">Selecione um funcionário</option>
            {data.employees.map(e => <option value={e.id} key={e.id}>{e.nome}{e.manager ? ' (Representante legal)' : ''}</option>)}
          </Form.Select>
        </Form.Group>
        {employee && (employee.manager ? <Alert variant="secondary">Este usuário possui acesso a todas as unidades. Seus acessos não podem ser restringidos por este cadastro.</Alert> : <>
          <Form.Check id="unit-access-inherit" label="Usar o padrão: somente a unidade principal" checked={inherit} disabled={busy} onChange={e => setInherit(e.target.checked)} />
          {!inherit && <Row className="g-3 my-2">{data.units.map(unit => <Col md={6} key={unit.id}><div className="border rounded p-3 h-100"><Form.Check id={'unit-access-' + unit.id} label={<>{unit.nome} {unit.is_primary && <Badge bg="primary">Principal</Badge>} {!unit.active && <Badge bg="secondary">Inativa</Badge>}</>} checked={selected.includes(unit.id)} disabled={busy || !unit.active} onChange={e => setSelected(ids => e.target.checked ? [...ids, unit.id] : ids.filter(id => id !== unit.id))} /></div></Col>)}</Row>}
          {!inherit && !selected.some(id => data.units.some(u => u.id === id && u.is_primary)) && <Alert variant="warning" className="mt-3">Sem acesso à unidade principal, o funcionário utilizará as filiais selecionadas, conforme as permissões de seu perfil.</Alert>}
          <Form.Group controlId="unit-access-reason" className="my-3"><Form.Label>Motivo da alteração</Form.Label><Form.Control as="textarea" rows={2} required maxLength={1000} value={reason} disabled={busy} onChange={e => setReason(e.target.value)} /></Form.Group>
          <Button type="submit" disabled={busy || !reason.trim()}>{busy ? 'Salvando...' : 'Salvar acessos'}</Button>
        </>)}
        {employee && data.payroll_ready && <div className="border-top mt-4 pt-3">
          <h4 className="h6">Unidade responsável pela folha</h4>
          <p className="text-muted">O acesso a várias unidades não duplica o salário. A folha deste funcionário será gerada na unidade abaixo; os históricos anteriores são preservados.</p>
          <Form.Group controlId="payroll-unit"><Form.Label>Unidade da folha</Form.Label><Form.Select value={payrollUnit} disabled={busy} onChange={e => setPayrollUnit(e.target.value)}><option value="">Principal (padrão)</option>{data.units.filter(u => u.active && !u.is_primary).map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}</Form.Select></Form.Group>
          {employee.manager && <Form.Group className="my-3" controlId="payroll-reason"><Form.Label>Motivo da alteração</Form.Label><Form.Control required value={reason} maxLength={1000} onChange={e => setReason(e.target.value)} /></Form.Group>}
          <Button type="button" className="mt-3" variant="outline-primary" disabled={busy || !reason.trim()} onClick={savePayroll}>Salvar unidade da folha</Button>
        </div>}
      </Form>}
    </Card.Body>
  </Card>;
}
